import React, { useEffect, useState } from 'react';
import CSFilter from './filter';
import { Button, Table, TableData, TableRow } from '@cokitchen/cokitchen-components';
import moment from 'moment';
import { DeleteIcon, EditIcon } from 'cokitchen-icons';
import { Link } from 'react-router-dom';
import { ENUM_INBOUND_ROLE } from 'utils/enum';
import RoleTag from 'components/base/role-tag';
import DeleteRepModal from '../modals/delete-rep-modal';
import NewRepModal from '../modals/new-rep-modal';
import DeleteBulkRep from '../modals/delete-bulk-rep';
import { CS_ROUTE_KEYS } from 'utils/constants';
import { UserType } from 'models/user.model';
import { apiService } from 'utils/api';
import { getUserName } from 'utils/functions';

export type RepListType = {
  id: string;
  first_name: string;
  last_name: string;
  date_added: string;
  role: ENUM_INBOUND_ROLE;
  status: 'online' | 'offline';
  last_online: string;
  email: string;
  phone_no: string;
};

const OnlineStatusTag = ({ type }: { type: 'online' | 'offline' }) => {
  const styles = {
    online: {
      bgColor: 'bg-green-5',
      textColor: 'text-green-5'
    },
    offline: {
      bgColor: 'bg-gray-5',
      textColor: 'text-gray-4'
    }
  };
  return (
    <div className={`w-fit text-sm  ${styles[type].textColor} flex items-center gap-1`}>
      <div className={`w-1 h-1 rounded-full ${styles[type].bgColor}`} />
      {type === 'offline' ? 'Offline' : 'Online'}
    </div>
  );
};

const TABLE_HEADERS = ['Name', 'Date added', 'Role', 'Status', 'Last online', ''];

const CSReps = () => {
  const [roleFilter, setRoleFilter] = useState<ENUM_INBOUND_ROLE | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [allReps, setAllReps] = useState([] as UserType[]);
  const [filteredReps, setFilteredReps] = useState([] as UserType[]);
  const [selectedReps, setSelectedReps] = useState<string[] | null>(null);
  const [singleRep, setSingleRep] = useState<UserType>();
  const [showDelRep, setShowDelRep] = useState(false);
  const [showNewRepModal, setshowNewRepModal] = useState(false);
  const [showBulkDel, setShowBulkDel] = useState(false);
  const [showFilter, setShowFilter] = useState(true);
  const [showButtons, setShowButtons] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [step, setStep] = useState(0);

  const toggleDel = (refetchData?: boolean) => {
    if (refetchData) getAllReps();
    setShowBulkDel(!showBulkDel);
  };

  const toggleModal = (refetchData?: boolean) => {
    if (refetchData) getAllReps();
    setshowNewRepModal(!showNewRepModal);
  };

  const delModal = (refetchData?: boolean) => {
    if (refetchData) getAllReps();
    setShowDelRep(!showDelRep);
  };

  const getAllReps = async () => {
    if (step === 0) setLoading(true);
    try {
      const res = await apiService.get('cs/staff/all', {
        params: { page: pageNumber },
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      setAllReps(res.data.data);
      setFilteredReps(res.data.data);
      const totalNoOfPages = Math.ceil(res.data.pagination.total / 10);
      setPageCount(totalNoOfPages);
    } finally {
      setLoading(false);
    }
  };

  const getSelectedPage = (val: { selected: number }) => {
    const pageNo = val?.selected + 1;
    setPageNumber(pageNo);
    setStep(step + 1);
  };
  useEffect(() => {
    getAllReps();
  }, [pageNumber]);

  useEffect(() => {
    setStep(0);
  }, []);

  const handleSearch = () => {
    const filtered = allReps.filter(
      ({ last_name, first_name }) =>
        last_name.toLocaleLowerCase().includes(searchValue) ||
        first_name.toLocaleLowerCase().includes(searchValue)
    );

    return filtered;
  };

  const filterByRole = () => {
    const result = handleSearch();
    const filtered = result?.filter(({ role }) => role === roleFilter);
    if (roleFilter !== null) {
      setFilteredReps(filtered);
    } else {
      setFilteredReps(result);
    }
  };

  const toggleAll = () => {
    const selected_ids = filteredReps.map(({ id }) => id);
    if (selectedReps && selectedReps?.length > 0) {
      setShowFilter(false);
      setShowButtons(true);
      setSelectedReps(null);
    } else {
      setSelectedReps(selected_ids);
    }
  };

  const bulkDel = () => {
    if (selectedReps && selectedReps?.length > 0) {
      toggleDel();
    } else {
      null;
    }
  };

  const toggleFilter = () => {
    if (selectedReps && selectedReps?.length > 0) {
      setShowFilter(false);
      setShowButtons(true);
    } else {
      setShowFilter(true);
      setShowButtons(false);
    }
  };

  useEffect(() => {
    toggleFilter();
  });

  const isAllSelected = () => {
    return selectedReps?.length === filteredReps?.length;
  };

  const updateSingleRep = (repId: string) => {
    const exists = selectedReps?.find((id) => id === repId);

    if (exists) {
      const filtered = selectedReps?.filter((id) => id !== repId);
      if (filtered) setSelectedReps(filtered);
    } else {
      setSelectedReps([...(selectedReps ?? []), repId]);
    }
  };

  const showEditIcon = (item: UserType) => {
    return (
      selectedReps === null ||
      selectedReps?.length === 0 ||
      (selectedReps?.length === 1 && selectedReps[0] === item.id)
    );
  };

  useEffect(() => {
    filterByRole();
  }, [roleFilter, searchValue]);

  const getUrl = (role: ENUM_INBOUND_ROLE, id: string) => {
    switch (role) {
      case ENUM_INBOUND_ROLE.CS_INBOUND:
      case ENUM_INBOUND_ROLE.CS:
      case ENUM_INBOUND_ROLE.CS_OUTBOUND:
      case ENUM_INBOUND_ROLE.CS_ADMIN:
      case ENUM_INBOUND_ROLE.CS_SUPER_ADMIN:
        return `/${CS_ROUTE_KEYS.CS_REPS}/${id}?role=${role}`;
      default:
        return `/${CS_ROUTE_KEYS.SETTINGS}/${CS_ROUTE_KEYS.CS_REPS}`;
    }
  };
  const getRole = (role: string) => {
    switch (role) {
      case 'CUSTOMER_SERVICE_INBOUND':
        return ENUM_INBOUND_ROLE.CS_INBOUND;
      case 'CUSTOMER_SERVICE_OUTBOUND':
        return ENUM_INBOUND_ROLE.CS_OUTBOUND;
      case 'CUSTOMER_SERVICE':
        return ENUM_INBOUND_ROLE.CS;
      case 'CUSTOMER_SERVICE_LEAD':
        return ENUM_INBOUND_ROLE.CS_SUPER_ADMIN;
      case 'CUSTOMER_SERVICE_ADMIN':
        return ENUM_INBOUND_ROLE.CS_ADMIN;

      default:
        return ENUM_INBOUND_ROLE.CS_OUTBOUND;
    }
  };

  const filterButton = () => {
    setShowFilter(true);
    setShowButtons(false);
  };

  const handleSelectAll = () => {
    const selected_ids = filteredReps?.map(({ id }) => id);
    setSelectedReps(selected_ids);
  };

  const handleEdit = (item: UserType) => {
    setSingleRep(item);
    setshowNewRepModal(true);
  };

  const handleDelete = (item: UserType) => {
    setSingleRep(item);
    setShowDelRep(true);
  };

  return (
    <div className='w-full space-y-8'>
      <div>
        {showFilter && (
          <CSFilter
            searchValue={searchValue}
            roleFilter={roleFilter}
            setRoleFilter={setRoleFilter}
            setSearchValue={setSearchValue}
          />
        )}

        {showButtons && (
          <div className='flex gap-[18px]'>
            <Button
              onClick={() => {
                setSelectedReps(null);
                filterButton();
              }}
              color='transparent'
              variant='block'
              className='w-[80px] h-[36px] rounded-[6px] text-[#5E6164] bg-[#F2F2F2]'
              small>
              Cancel
            </Button>

            <Button
              onClick={handleSelectAll}
              color='transparent'
              variant='block'
              className='w-[93px] h-[36px] rounded-[6px] text-[#5E6164] bg-[#F2F2F2]'
              small>
              Select all
            </Button>

            <Button
              onClick={bulkDel}
              color='transparent'
              variant='block'
              className='w-[93px] h-[36px] rounded-[6px] text-[#E60A2B] border border-[#E60A2B] bg-[#FFFFFF]'
              small>
              Delete
            </Button>
          </div>
        )}
      </div>
      {loading ? (
        <div className='text-xl leading-[40vh] text-center'>Loading...</div>
      ) : filteredReps?.length === 0 ? (
        <div className='text-xl leading-[40vh] text-center'>No cs reps</div>
      ) : (
        <div className='no-scroll-bar whitespace-nowrap pb-10'>
          <Table
            isAllSelected={isAllSelected()}
            onToggleAll={toggleAll}
            headers={TABLE_HEADERS}
            onPageChange={getSelectedPage}
            pageCount={pageCount}
            canSelect>
            {filteredReps?.map((item, idx) => (
              <TableRow
                isSelected={selectedReps?.includes(item.id)}
                onToggle={() => updateSingleRep(item.id)}
                canSelect
                key={idx}>
                <TableData>
                  <Link className='cursor-pointer' to={getUrl(getRole(item.role), item.id)}>
                    {getUserName(item)}
                  </Link>
                </TableData>
                <TableData className='px-5 text-gray-5'>
                  <span>{moment(item.created_at).format('Do MMM, YYYY')}</span>
                </TableData>
                <TableData>
                  <RoleTag type={getRole(item.role)} />
                </TableData>
                <TableData>
                  <OnlineStatusTag type={item.online ? 'online' : 'offline'} />
                </TableData>
                <TableData className='px-5 text-gray-5'>
                  <span>{moment(item.updated_at).format('Do MMM, YYYY hh:mm a')}</span>
                </TableData>

                <TableData className='flex items-center gap-3'>
                  <>
                    {showEditIcon(item) && (
                      <EditIcon
                        onClick={() => handleEdit(item)}
                        className={`cursor-pointer text-gray-5 hover:bg-gray-1 w-5 h-5 transition-all p-[3px] rounded`}
                      />
                    )}
                    <DeleteIcon
                      onClick={() => handleDelete(item)}
                      className='cursor-pointer text-red-5 hover:bg-red-1 w-5 h-5 transition-all p-[3px] rounded'
                    />
                  </>
                </TableData>
              </TableRow>
            ))}
          </Table>
        </div>
      )}

      <DeleteRepModal show={showDelRep} onClose={delModal} singleRep={singleRep} />

      {showNewRepModal && (
        <NewRepModal
          show={showNewRepModal}
          onClose={toggleModal}
          type='edit'
          singleRep={singleRep}
        />
      )}

      <DeleteBulkRep show={showBulkDel} onClose={toggleDel} data={selectedReps} />
    </div>
  );
};

export default CSReps;
